import { useMemo, useCallback, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { SelectedOfferingSide } from "@Shape-Digital/kudzu-data/lib/types/common";

import { SelectChangeEvent } from "../../Unknown/Select";

import { Option as SelectCenterServiceInputOption } from "./input";
import {
  CenterConfig,
  CenterServiceGroupId,
  CenterServicesState,
  SelectedCenterService,
  AddOnsState,
} from "./types";
import useCenterServices from "./useCenterServices";
import getTotalSummary from "./getTotalSummary";
import useAddOns from "./useAddOns";

interface InitialServiceProps {
  id: string;
  side?: SelectedOfferingSide;
  addOnIds: string[];
  addOnQuantities?: Record<string, number>;
  studyIds: string[];
}

interface UseCenterServiceOptionsProps {
  centerConfig?: CenterConfig;
  initialServices: InitialServiceProps[];
  initialAddOnsIds: string[];
  initialAddOnQuantities?: Record<string, number>;
  couponAmount: number;
  couponAddOnId?: string | null;
}

const useCenterServiceOptions = ({
  centerConfig,
  initialServices,
  initialAddOnsIds,
  initialAddOnQuantities,
  couponAmount,
  couponAddOnId,
}: UseCenterServiceOptionsProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedCenterServices, setSelectedCenterServices] =
    useState<CenterServicesState>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<AddOnsState>([]);

  const { formatNumber } = useIntl();
  const {
    dataState: centerServices,
    offerings,
    packages,
  } = useCenterServices({
    centerConfig,
    includeIds: useMemo(
      () => initialServices.map((service) => service.id),
      [initialServices],
    ),
  });

  const { data: addOns, status: addOnsStatus } = useAddOns(centerConfig?.id);

  const centerServiceList = useMemo(() => {
    const { data, status } = centerServices;

    if (status !== "success") return [];

    return Object.entries(data).map(([id, centerService]) => ({
      value: id,
      ...centerService,
    }));
  }, [centerServices]);

  const studiesCount = useMemo(() => {
    return selectedCenterServices.reduce((count, curr) => {
      return count + (curr?.studyIds?.length || 0);
    }, 0);
  }, [selectedCenterServices]);

  useEffect(() => {
    setSelectedAddOns((prev) => {
      return prev.map((addOn) => {
        const price = addOn.isPerStudyPricing
          ? addOn.unitPrice * Math.max(studiesCount, 1) * addOn.quantity
          : addOn.unitPrice * addOn.quantity;
        return { ...addOn, price };
      });
    });
  }, [studiesCount]);

  const options = useMemo<SelectCenterServiceInputOption[]>(() => {
    const result = centerServiceList.reduce<SelectCenterServiceInputOption[]>(
      (acc, centerService) => {
        const { groupId, id, name, price, description, discountPrice } =
          centerService;

        const isDual =
          centerService.groupId === "single-offering" &&
          centerService.scanSide === "dual";

        const alreadySelectedItems = selectedCenterServices.filter(
          (selectedCenterService) =>
            selectedCenterService?.value &&
            centerService?.id === selectedCenterService.value,
        );

        const isDisabled = isDual
          ? alreadySelectedItems.length === 2
          : alreadySelectedItems.length === 1;

        const item = {
          groupId,
          value: id,
          label: name,
          price: formatNumber(price, {
            style: "currency",
            currency: centerConfig?.defaultCurrencyCode,
          }),
          description,
          discountPrice: formatNumber(discountPrice, {
            style: "currency",
            currency: centerConfig?.defaultCurrencyCode,
          }),
          isDisabled,
        };

        return [...acc, item];
      },
      [],
    );
    return result;
  }, [
    centerServiceList,
    selectedCenterServices,
    formatNumber,
    centerConfig?.defaultCurrencyCode,
  ]);

  const initialSelectedCenterServices = useMemo(() => {
    return initialServices.map((initialServiceProps) => {
      const initialServiceOption = centerServiceList.find(
        (centerServiceItem) => centerServiceItem.id === initialServiceProps.id,
      );

      if (!initialServiceOption) return null;

      return {
        ...initialServiceOption,
        localId: uuidv4(),
        addOnIds: initialServiceProps.addOnIds,
        addOnQuantities: initialServiceProps.addOnQuantities,
        studyIds: initialServiceProps.studyIds,
        scanSide: "dual",
        side: initialServiceProps.side,
      };
    });
  }, [initialServices, centerServiceList]);

  const initialSelectedAddOns = useMemo(() => {
    return (
      addOns
        ?.filter((addOn) => initialAddOnsIds.includes(addOn.id))
        .map((addOn) => ({
          ...addOn,
          quantity: initialAddOnQuantities?.[addOn.id] ?? 1,
        })) || []
    );
  }, [addOns, initialAddOnsIds, initialAddOnQuantities]);

  const initialize = useCallback(() => {
    setSelectedCenterServices(initialSelectedCenterServices);
    setSelectedAddOns(initialSelectedAddOns || []);
    setIsInitialized(true);
  }, [initialSelectedCenterServices, initialSelectedAddOns]);

  useEffect(() => {
    if (
      !isInitialized &&
      centerServiceList.length &&
      addOnsStatus === "success"
    ) {
      initialize();
    }
  }, [
    centerServiceList,
    initialServices,
    isInitialized,
    addOns,
    initialAddOnsIds,
    initialSelectedCenterServices,
    initialSelectedAddOns,
    initialize,
    addOnsStatus,
  ]);

  const selectedOptions = useMemo(() => {
    return selectedCenterServices.map((selectedCenterService) => {
      const { data, status } = centerServices;

      if (!selectedCenterService || status !== "success") return null;

      const centerService = data[selectedCenterService.value];

      if (!centerService) return null;

      const { groupId, id, name, price, description, discountPrice } =
        centerService;

      const isDual =
        centerService.groupId === "single-offering" &&
        centerService.scanSide === "dual";

      const result: SelectCenterServiceInputOption & { localId: string } = {
        groupId,
        value: id,
        label: name,
        localId: selectedCenterService.localId,
        price: formatNumber(price, {
          style: "currency",
          currency: centerConfig?.defaultCurrencyCode,
        }),
        description,
        discountPrice: formatNumber(discountPrice, {
          style: "currency",
          currency: centerConfig?.defaultCurrencyCode,
        }),
        isDisabled: true,
        metadata: {
          isDual,
          side: selectedCenterService.side || "",
        },
      };

      return result;
    });
  }, [
    centerConfig?.defaultCurrencyCode,
    centerServices,
    formatNumber,
    selectedCenterServices,
  ]);

  const isAllCenterServicesValid = useMemo(() => {
    const { data, status } = centerServices;

    if (status !== "success") return false;

    return selectedCenterServices.every((selectedCenterService) => {
      if (!selectedCenterService) return false;

      const centerService = data[selectedCenterService.value];

      if (!centerService) return false;

      const isCenterServiceWithDualSide =
        centerService.groupId === "single-offering" &&
        centerService.scanSide === "dual";

      if (isCenterServiceWithDualSide && !selectedCenterService.side) {
        return false;
      }

      return true;
    });
  }, [centerServices, selectedCenterServices]);

  const isDirty = useMemo(() => {
    if (initialSelectedCenterServices.length !== selectedCenterServices.length)
      return true;

    if (initialAddOnsIds.length !== selectedAddOns.length) return true;

    if (
      selectedAddOns.some((addOn) => {
        if (!initialAddOnsIds.includes(addOn.id)) return true;

        const initialQuantity = initialAddOnQuantities?.[addOn.id] ?? 1;
        return addOn.quantity !== initialQuantity;
      })
    ) {
      return true;
    }

    const isCenterServicesDirty = selectedCenterServices.some((option) => {
      const initialOption = initialSelectedCenterServices.find(
        (initialOptionItem) => initialOptionItem?.value === option?.value,
      );

      if (!initialOption) return true;

      const isOptionDirty = (initialOption.side || "") !== (option?.side || "");

      const isAddOnsDirty =
        (initialOption.addOnIds || []).sort().toString() !==
        (option?.addOnIds || []).sort().toString();

      const isAddOnQuantitiesDirty = (option?.addOnIds || []).some(
        (addOnId) => {
          const initialQuantity = initialOption.addOnQuantities?.[addOnId] ?? 1;
          const currentQuantity = option?.addOnQuantities?.[addOnId] ?? 1;
          return initialQuantity !== currentQuantity;
        },
      );

      return isOptionDirty || isAddOnsDirty || isAddOnQuantitiesDirty;
    });

    return isCenterServicesDirty;
  }, [
    initialAddOnsIds,
    initialAddOnQuantities,
    initialSelectedCenterServices,
    selectedAddOns,
    selectedCenterServices,
  ]);

  const isCanCreateMore = useMemo(() => {
    const { status, data } = centerServices;

    if (status !== "success") return false;

    const selectableCount = Object.keys(data).reduce(
      (acc, centerServiceKey) => {
        const centerService = data[centerServiceKey];

        if (!centerService) return acc;
        const isDual =
          centerService.groupId === "single-offering" &&
          centerService.scanSide === "dual";

        return isDual ? acc + 2 : acc + 1;
      },
      0,
    );

    return selectedOptions.length !== selectableCount;
  }, [centerServices, selectedOptions]);

  const addCenterService = useCallback(() => {
    setSelectedCenterServices((prev) => [...prev, null]);
  }, [setSelectedCenterServices]);

  const removeCenterService = useCallback(
    (index: number) => {
      setSelectedCenterServices((prev) => {
        const list = prev.filter((_, idx) => idx !== index);
        return list.length ? list : [null];
      });
    },
    [setSelectedCenterServices],
  );

  const changeCenterService = useCallback(
    (index: number) => {
      return (option: SelectCenterServiceInputOption) => {
        const { status, data } = centerServices;

        if (status !== "success") {
          return;
        }

        const { value, groupId } = option;

        const centerService = data[value];

        if (!centerService) {
          setSelectedCenterServices((prev) =>
            prev.map((item, idx) => (idx === index ? null : item)),
          );
          return;
        }

        const {
          name,
          price,
          discountPrice,
          durationMinutes,
          addOnIds,
          studyIds,
        } = centerService;

        if (!option) {
          setSelectedCenterServices((prev) =>
            prev.map((item, idx) => (idx === index ? null : item)),
          );
        }

        const selectedCenterService: SelectedCenterService = {
          value,
          localId: uuidv4(),
          groupId: groupId as CenterServiceGroupId,
          name,
          price,
          addOnIds,
          studyIds,
          discountPrice,
          durationMinutes,
        };

        setSelectedCenterServices((prev) =>
          prev.map((item, idx) =>
            idx === index ? selectedCenterService : item,
          ),
        );
      };
    },
    [centerServices, setSelectedCenterServices],
  );

  const changeSelectedSide = useCallback(
    (index: number) => {
      return (event: SelectChangeEvent<string | number | boolean | null>) => {
        setSelectedCenterServices((prev) =>
          prev.map((item, idx) => {
            if (idx !== index || !item) return item;

            return {
              ...item,
              side: event.target.value as SelectedOfferingSide,
            };
          }),
        );
      };
    },
    [setSelectedCenterServices],
  );

  const appointmentAddOnIds = selectedAddOns.map((addOn) => addOn.id);
  const studyAddOnIds = selectedCenterServices.flatMap((service) => {
    return service?.addOnIds || [];
  });

  const addOnIds = [...appointmentAddOnIds, ...studyAddOnIds];

  const isCouponActive = !couponAddOnId || addOnIds.includes(couponAddOnId);

  const couponAddOnQuantity = useMemo(() => {
    if (!couponAddOnId) return 1;

    const flatQuantities = selectedAddOns
      .filter((addOn) => addOn.id === couponAddOnId)
      .map((addOn) => addOn.quantity);

    const serviceQuantities = selectedCenterServices
      .map((service) => service?.addOnQuantities?.[couponAddOnId])
      .filter((quantity): quantity is number => !!quantity);

    const quantities = [...flatQuantities, ...serviceQuantities];

    return quantities.length ? Math.min(...quantities) : 1;
  }, [couponAddOnId, selectedAddOns, selectedCenterServices]);

  const liveCouponAmount = useMemo(() => {
    if (!couponAddOnId) return couponAmount;

    const couponAddOn = addOns?.find((addOn) => addOn.id === couponAddOnId);
    if (!couponAddOn) return couponAmount;

    return couponAddOn.unitPrice * couponAddOnQuantity;
  }, [addOns, couponAddOnId, couponAddOnQuantity, couponAmount]);

  const summary = getTotalSummary({
    centerServices: selectedCenterServices.filter(
      (centerService): centerService is SelectedCenterService =>
        centerService !== null,
    ),
    discountAmount: centerConfig?.discountAmount,
    addOns: selectedAddOns,
    centerAddOns: addOns || [],
    couponAmount: isCouponActive ? liveCouponAmount : 0,
  });

  return {
    summary,
    options,
    packages,
    offerings,
    addOns,
    isDirty,
    isInitialized,
    selectedOptions,
    isCanCreateMore,
    isAllCenterServicesValid,
    selectedAddOns,
    selectedCenterServices,
    setSelectedCenterServices,
    initialize,
    setSelectedAddOns,
    addCenterService,
    removeCenterService,
    changeCenterService,
    changeSelectedSide,
  };
};

export default useCenterServiceOptions;
